import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { NewsArticleNumList } from '@/features/actualidad-hostelera/NewsArticleNumList'
import { createNewsArticleSourceChip } from '@/features/actualidad-hostelera/NewsArticleSourceChip'
import { newsArticlePath, type NewsArticleBodyProps } from '@/features/actualidad-hostelera/articles'
import { toOfficialSalesWhatsAppHref } from '@/lib/officialContact'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import '@/styles/food-cost-fonts.css'

const k = (f: string) => `news.articles.por-que-hosteleria-tarda-digitalizarse-control-costes.${f}`

const REF = {
    infohoreca: {
        href: 'https://www.infohoreca.com/noticias/20260803/makro-soluciones-digitales-hosteleria-crecimiento-2026',
        domain: 'infohoreca.com',
        label: 'InfoHoreca',
    },
    hosteleriadigital: {
        href: 'https://www.hosteleriadigital.es/2026/07/31/la-hosteleria-espanola-acelera-su-digitalizacion-ante-un-verano-record-y-la-proxima-entrada-en-vigor-de-verifactu/',
        domain: 'hosteleriadigital.es',
        label: 'Hostelería Digital',
    },
    hosteleriamadrid: {
        href: 'https://www.hosteleriamadrid.com/blog/fiscal/hacienda-retrasa-hasta-2027-implantacion-verifactu-pymes-autonomos/',
        domain: 'hosteleriamadrid.com',
        label: 'Hostelería Madrid',
    },
    wolterskluwer: {
        href: 'https://www.wolterskluwer.com/es-es/expert-insights/verifactu-que-es',
        domain: 'wolterskluwer.com',
        label: 'Wolters Kluwer',
    },
    anuario: {
        href: 'https://hosteleriadeespana.es/publicaciones-hosteleria.html',
        domain: 'hosteleriadeespana.es',
        label: 'Anuario Hostelería de España',
    },
} as const

const SourceChip = createNewsArticleSourceChip(REF, k)

export function DigitalizacionHosteleriaArticle({ article: meta }: NewsArticleBodyProps) {
    const { t } = useTranslation()

    const contactWhatsapp = toOfficialSalesWhatsAppHref(
        t(
            k('contactWhatsapp'),
            'Hola, he leído el artículo sobre digitalización y control de costes en hostelería y me gustaría revisar el margen real de mi carta.',
        ),
    )

    const weekActions: ReactNode[] = [
        t(
            k('weekActions.1'),
            'Saca los tres platos que más vendes y comprueba cuándo actualizaste su escandallo por última vez.',
        ),
        t(
            k('weekActions.2'),
            'Compara el precio de tus tres proveedores principales de esta semana con el de hace seis meses.',
        ),
        t(
            k('weekActions.3'),
            'Si tu TPV te da ventas por plato, crúzalas con el coste real de cada receta, no con lo que crees que cuesta.',
        ),
        t(
            k('weekActions.4'),
            'Revisa si tu programa de facturación ya está adaptado a Verifactu o si tendrás que cambiarlo antes de julio de 2027.',
        ),
        t(
            k('weekActions.5'),
            'Calcula cuánto tiempo dedicas al mes a saber si ganas dinero de verdad, y compáralo con el que dedicas a que la barra funcione.',
        ),
        t(
            k('weekActions.6'),
            'Si la respuesta es «casi nada», prueba una herramienta que no te pida parar el servicio para darte esa cifra.',
        ),
    ]

    const sources = [
        {
            label: t(
                k('sources.1'),
                'Makro registra un aumento del 63% en la adopción de soluciones digitales para hostelería, InfoHoreca',
            ),
            href: REF.infohoreca.href,
        },
        {
            label: t(
                k('sources.2'),
                'La hostelería española acelera su digitalización ante un verano récord y la próxima entrada en vigor de Verifactu, Hostelería Digital',
            ),
            href: REF.hosteleriadigital.href,
        },
        {
            label: t(
                k('sources.3'),
                'Hacienda retrasa hasta 2027 la implantación de Verifactu para pymes y autónomos, Hostelería Madrid',
            ),
            href: REF.hosteleriamadrid.href,
        },
        {
            label: t(k('sources.4'), 'Verifactu: qué es y cómo afecta a tu facturación, Wolters Kluwer'),
            href: REF.wolterskluwer.href,
        },
    ]

    return (
        <div className={article.page}>
            <article className={article.shell}>
                <ArticleCoverHero
                    category={meta.category}
                    title={meta.title}
                    dek={
                        <>
                            {t(k('dek.before'), 'Las altas en soluciones digitales para hostelería crecieron un ')}
                            <strong>63%</strong>
                            {t(k('dek.mid1'), ' este último año, el TPV en la nube se disparó un ')}
                            <strong>184%</strong>
                            {t(k('dek.mid2'), ' y Verifactu ya tiene fecha límite para los autónomos: el ')}
                            <strong>{t(k('dek.deadline'), '1 de julio de 2027')}</strong>
                            {t(
                                k('dek.after'),
                                '. La parte del negocio que de verdad decide si ganas dinero, el escandallo, el control de costes, el margen real por plato, sigue sin subirse al carro.',
                            )}
                        </>
                    }
                    imageSrc={meta.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'TPV en la barra de un bar junto a facturas de papel y una libreta que nadie está usando',
                    )}
                    byline={
                        <ArticleByline
                            authorName={meta.authorName ?? 'Samuel Carrillo'}
                            readingMinutes={meta.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />

                <div className={article.proseIntro}>
                    <p className={article.p}>
                        {t(
                            k('intro.1'),
                            '¿Quién no conoce a un hostelero que juró que jamás cambiaría la máquina registradora por un TPV? «Total, con la caja y una libreta me sobra.» Hoy ese mismo hostelero no cobra una caña sin pasarla por la tablet.',
                        )}
                    </p>
                    <p className={article.p}>
                        {t(
                            k('intro.2'),
                            'Le costó, pero se subió al carro. El TPV le dio algo que podía tocar desde el primer día: la cuenta sale más rápido, el camarero no se come el cambio, y de paso queda un registro de cada venta sin que nadie tenga que anotarla.',
                        )}
                    </p>
                    <p className={cn(article.p, article.pGap2xl)}>
                        {t(
                            k('intro.3'),
                            'Pregúntale ahora por el escandallo de la carta, por cuánto le cuesta de verdad ese plato que más vende, y lo más probable es que saque una libreta, o el recuerdo de una que tuvo hace tres años.',
                        )}
                    </p>
                </div>

                <section id='en-cristiano'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.enCristiano'), 'TPV en la nube dispara y Verifactu ya tiene fecha')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            <span className={article.cite}>
                                {t(
                                    k('cristiano.p1'),
                                    'Según los datos de Makro, la digitalización de bares y restaurantes no para de crecer:',
                                )}
                                <SourceChip source='infohoreca' />
                            </span>
                        </p>
                    </div>
                    <div className={article.tableWrap}>
                        <table className={article.table}>
                            <thead>
                                <tr>
                                    <th scope='col'>{t(k('table.tools.col1'), 'Herramienta')}</th>
                                    <th scope='col'>{t(k('table.tools.col2'), 'Crecimiento (ejercicio 2024/2025)')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{t(k('table.tools.row1.label'), 'Altas totales en soluciones Dish')}</td>
                                    <td>
                                        <strong>+63%</strong>
                                        {t(k('table.tools.row1.after'), ' (3.581 → 5.820)')}
                                    </td>
                                </tr>
                                <tr>
                                    <td>{t(k('table.tools.row2.label'), 'TPV en la nube (Dish POS)')}</td>
                                    <td>
                                        <strong>+184%</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td>{t(k('table.tools.row3.label'), 'Gestión de reservas (Dish Reservation)')}</td>
                                    <td>
                                        <strong>+74%</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td>{t(k('table.tools.row4.label'), 'Ventas por canal digital de Makro')}</td>
                                    <td>
                                        <strong>+42%</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td>{t(k('table.tools.row5.label'), 'Clientes que compraron vía Marketplace')}</td>
                                    <td>
                                        <strong>75%</strong>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className={article.prose}>
                        <p className={article.p}>
                            <span className={article.cite}>
                                {t(
                                    k('cristiano.p2'),
                                    'Y Verifactu, el sistema de facturación verificable de la Agencia Tributaria, ya tiene calendario fijado tras el aplazamiento de finales de 2025:',
                                )}
                                <SourceChip source='hosteleriamadrid' />
                            </span>
                        </p>
                    </div>
                    <div className={cn(article.tableWrap, 'mb-10')}>
                        <table className={article.table}>
                            <thead>
                                <tr>
                                    <th scope='col'>{t(k('table.verifactu.col1'), 'Quién')}</th>
                                    <th scope='col'>{t(k('table.verifactu.col2'), 'Fecha límite')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{t(k('table.verifactu.row1.label'), 'Impuesto sobre Sociedades (pymes)')}</td>
                                    <td>
                                        <strong>{t(k('table.verifactu.row1.date'), '1 de enero de 2027')}</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td>{t(k('table.verifactu.row2.label'), 'Autónomos y el resto de empresas')}</td>
                                    <td>
                                        <strong>{t(k('table.verifactu.row2.date'), '1 de julio de 2027')}</strong>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <section id='detalle'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.detalle'), 'Autónomos: Verifactu es el 1 de julio de 2027, no enero')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(
                                k('detalle.p1.before'),
                                'Las dos noticias que corren estos días dicen, sin matiz, que Verifactu «entra en vigor el 1 de enero de 2027». Esa fecha es real, pero solo para los contribuyentes del Impuesto sobre Sociedades. Si eres autónomo, el caso de la inmensa mayoría de la hostelería independiente, tienes hasta el ',
                            )}
                            <strong>{t(k('detalle.p1.deadline'), '1 de julio de 2027')}</strong>
                            {t(k('detalle.p1.after'), ', seis meses más.')}
                        </p>
                        <p className={article.p}>
                            <span className={article.cite}>
                                {t(
                                    k('detalle.p2'),
                                    'Y ese calendario tampoco es el original. Hacienda lo aplazó un año completo en diciembre de 2025, mediante el Real Decreto-ley 15/2025, después de que las patronales avisaran de que no daba tiempo a adaptar los sistemas de facturación ni los propios terminales de venta.',
                                )}
                                <SourceChip source='hosteleriamadrid' />
                            </span>
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            <span className={article.cite}>
                                {t(
                                    k('detalle.p3.before'),
                                    'Ni con fecha en el BOE nos damos prisa: según el Barómetro de la Asesoría 2024 de Wolters Kluwer, más de la mitad de las empresas (',
                                )}
                                <strong>54,2%</strong>
                                {t(
                                    k('detalle.p3.after'),
                                    ') todavía no había decidido cómo iba a cumplir con Verifactu.',
                                )}
                                <SourceChip source='wolterskluwer' />
                            </span>
                        </p>
                    </div>
                </section>

                <section id='mala-noticia'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.malaNoticia'), 'El récord de Makro no es el control de costes del sector')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(
                                k('mala.p1'),
                                'El «récord» que cuentan estas noticias es el crecimiento de un proveedor, Makro, no una foto del sector entero. Y sobre todo: no dice nada de si el negocio se gestiona mejor, solo de si se ha comprado una herramienta.',
                            )}
                        </p>
                        <p className={article.p}>
                            {t(
                                k('mala.p2'),
                                'Aquí está la diferencia de fondo entre el TPV y el resto de la gestión del negocio. Comprar un TPV con Verifactu integrado será obligatorio desde 2027. Llevar un control de costes serio, no lo es, nadie te multa por no saber el margen real de tu menú del día. El TPV, además, dejó de pedir esfuerzo extra en cuanto llegó a la nube: el dato se genera solo, vendiendo. El control de gestión tradicional pide justo lo contrario, que el dueño pare la barra para sentarse a meter números en un Excel pensado para el papeleo de una empresa grande, no para un bar de barrio.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(k('mala.p3.before'), 'Las encuestas del sector lo confirman: la falta de presupuesto (')}
                            <strong>35-38%</strong>
                            {t(
                                k('mala.p3.mid'),
                                ' de los hosteleros la cita como principal barrera) y la falta de conocimientos técnicos en el equipo (',
                            )}
                            <strong>59%</strong>
                            {t(
                                k('mala.p3.after'),
                                ') siguen frenando la digitalización de la gestión. Y muchos negocios que sí incorporan herramientas lo hacen sin una integración real que les simplifique el día a día, según los mismos estudios del sector.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='cuenta-vieja'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.cuentaVieja'), '5.820 altas son menos del 2% del sector')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            <span className={article.cite}>
                                {t(
                                    k('cuenta.p1.before'),
                                    '5.820 altas en soluciones Dish, por muy buen dato que sea para Makro, son menos del ',
                                )}
                                <strong>2%</strong>
                                {t(
                                    k('cuenta.p1.after'),
                                    ' de los más de 300.000 establecimientos de hostelería que hay en España (Anuario Hostelería de España). El TPV ya es prácticamente universal en el sector; lo que mide este crecimiento es la migración a la nube y la prisa de última hora por Verifactu, no un sector que empieza de cero.',
                                )}
                                <SourceChip source='anuario' />
                            </span>
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('cuenta.p2'),
                                'Del control de costes, en cambio, no existe una cifra sectorial equivalente. Ninguna encuesta se atreve a decir qué porcentaje de bares independientes conoce hoy el margen real de cada plato de su carta.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='ejemplo'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>{t(k('h2.ejemplo'), 'Un ejemplo con números')}</h2>
                    <div className={article.prose}>
                        <p className={cn(article.p, article.pItalic)}>
                            {t(
                                k('ejemplo.note'),
                                'Estimación propia a partir de un ticket medio y una estructura de costes típica de un bar de barrio; no está enlazada a ninguna fuente porque el cálculo es nuestro.',
                            )}
                        </p>
                        <p className={article.p}>
                            {t(
                                k('ejemplo.p1.before'),
                                'Un bar con 40 referencias en carta que factura 18.000 € al mes suele moverse con un margen bruto de referencia del ',
                            )}
                            <strong>65%</strong>
                            {t(k('ejemplo.p1.mid'), '. Si el coste de la materia prima sube un ')}
                            <strong>4%</strong>
                            {t(
                                k('ejemplo.p1.after'),
                                ', lo que ha pasado con el aceite, el café o la carne en los últimos dos años, y el dueño no revisa el escandallo ni toca el precio de venta, ese margen puede caer al ',
                            )}
                            <strong>61%</strong>
                            {t(k('ejemplo.p1.end'), ' sin que nadie lo note en la caja. Son cerca de ')}
                            <strong>700 €</strong>
                            {t(
                                k('ejemplo.p1.tail'),
                                ' al mes que se evaporan en una carta que, mirando la recaudación diaria, sigue pareciendo igual de rentable.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('ejemplo.p2'),
                                'El TPV no detecta esa fuga: te dice cuánto vendiste, no cuánto ganaste. Y la libreta tampoco, porque nadie tiene tiempo de recalcular 40 escandallos cada vez que sube el precio del aceite.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='esta-semana'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.estaSemana'), 'Qué digitalizar después del TPV')}
                    </h2>
                    <div className={article.prose}>
                        <NewsArticleNumList items={weekActions} />
                    </div>
                </section>

                <section id='pendientes'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.pendientes'), 'Si Hacienda vuelve a aplazar y cuántos llevan el margen al día')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(
                                k('pendientes.p1'),
                                'Hacienda ya aplazó Verifactu un año entero en diciembre de 2025. No hay garantía de que julio de 2027 sea, esta vez sí, la fecha definitiva.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('pendientes.p2'),
                                'Tampoco hay un dato público fiable sobre qué porcentaje de la hostelería independiente lleva de verdad un control de costes actualizado, frente a llevarlo de memoria. Las encuestas de digitalización del sector miden TPV, reservas y venta online; nadie mide todavía la gestión interna con el mismo rigor. Parte del argumento de este artículo se apoya, precisamente, en que ese dato no existe.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('pendientes.p3Before'),
                                'Sobre por qué esa digitalización entra por el BOE y no por el retorno —y qué haría distinto un agente tecnológico— está la tribuna ',
                            )}
                            <LocaleLink
                                to={newsArticlePath('digitalizacion-hosteleria-la-empuja-el-boe')}
                                className={article.link}
                            >
                                {t(
                                    k('pendientes.p3Link'),
                                    'La digitalización de la hostelería no la empuja el retorno. La empuja el BOE',
                                )}
                            </LocaleLink>
                            {t(k('pendientes.p3After'), '.')}
                        </p>
                    </div>
                </section>

                <div className={article.prose}>
                    <p className={article.p}>
                        {t(
                            k('closing.p1'),
                            'Si sospechas que en tu carta hay platos que crees rentables y no lo son, cuéntanos qué platos más vendes y lo miramos.',
                        )}
                    </p>
                    <p className={cn(article.p, article.pGap2xl)}>
                        {t(k('closing.p2'), 'El TPV ya llegó. El margen, todavía no.')}
                    </p>
                </div>

                <section className={article.cta}>
                    <h2 className={cn(article.h2, article.h2Cta)}>
                        {t(k('h2.cta'), '¿Sospechas que tu carta esconde platos que no son tan rentables?')}
                    </h2>
                    <p className={article.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Cuéntanos qué platos más vendes y te ayudamos a mirar el margen real, no el que crees que tienes.',
                        )}
                    </p>
                    <div className={article.ctaActionsCenter}>
                        <a
                            href={contactWhatsapp}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={cn(article.btn, article.btnSolid)}
                        >
                            {t(k('cta.whatsapp'), 'Escríbenos por WhatsApp')}
                        </a>
                    </div>
                </section>

                <section className={article.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={article.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={article.sourcesList}>
                        {sources.map(source => (
                            <li key={source.href}>
                                <a
                                    href={source.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={article.link}
                                >
                                    {source.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </section>
            </article>
        </div>
    )
}
